import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  MicrophoneIcon,
  XMarkIcon,
  SpeakerWaveIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useAppStore } from '@/store';

interface VoiceInterfaceProps {
  open: boolean;
  onClose: () => void;
}

type VoiceState = 'idle' | 'listening' | 'processing' | 'speaking' | 'error';

const VoiceInterface: React.FC<VoiceInterfaceProps> = ({ open, onClose }) => {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [lastCommand, setLastCommand] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  
  const addNotification = useAppStore((state) => state.addNotification);
  const recognitionRef = useRef<any>(null);
  const synthRef = useRef<any>(null);

  // Проверка поддержки браузером
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const speechSynthesis = window.speechSynthesis;
    
    setIsSupported(!!SpeechRecognition && !!speechSynthesis);
    
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'ru-RU';
      
      recognition.onstart = () => {
        setVoiceState('listening');
        setTranscript('');
        setError('');
      };
      
      recognition.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        setTranscript(finalTranscript || interimTranscript);
        
        if (finalTranscript) {
          processVoiceCommand(finalTranscript);
        }
      };
      
      recognition.onerror = (event: any) => {
        setError(`Ошибка распознавания: ${event.error}`);
        setVoiceState('error');
      };
      
      recognition.onend = () => {
        if (voiceState === 'listening') {
          setVoiceState('idle');
        }
      };
      
      recognitionRef.current = recognition;
    }
    
    if (speechSynthesis) {
      synthRef.current = speechSynthesis;
    }
  }, [voiceState]);

  // Обработка голосовых команд
  const processVoiceCommand = async (command: string) => {
    setVoiceState('processing');
    setLastCommand(command);
    
    try {
      // Простые команды для демонстрации
      let responseText = '';
      
      if (command.toLowerCase().includes('температура')) {
        responseText = 'Текущая температура 22 градуса, влажность 75 процентов';
      } else if (command.toLowerCase().includes('кормление') || command.toLowerCase().includes('покормил')) {
        responseText = 'Кормление записано успешно';
        addNotification({
          type: 'success',
          title: 'Кормление записано',
          message: 'Данные о кормлении сохранены в системе',
        });
      } else if (command.toLowerCase().includes('продажа') || command.toLowerCase().includes('заказ')) {
        responseText = 'Заказ создан и добавлен в систему';
        addNotification({
          type: 'success',
          title: 'Заказ создан',
          message: 'Новый заказ добавлен в список активных',
        });
      } else if (command.toLowerCase().includes('статистика') || command.toLowerCase().includes('отчет')) {
        responseText = 'Загружаю статистику фермы';
      } else if (command.toLowerCase().includes('стеллаж')) {
        const stackMatch = command.match(/стеллаж[е]?\s*(\d+)/i);
        const stackNumber = stackMatch ? stackMatch[1] : '1';
        responseText = `Показываю информацию по стеллажу номер ${stackNumber}`;
      } else {
        responseText = 'Команда не распознана. Попробуйте сказать "помощь" для списка доступных команд';
      }
      
      setResponse(responseText);
      await speakResponse(responseText);
      
    } catch (error) {
      console.error('Ошибка обработки команды:', error);
      setError('Ошибка обработки команды');
      setVoiceState('error');
    }
  };

  // Озвучивание ответа
  const speakResponse = async (text: string) => {
    if (!synthRef.current) return;
    
    setVoiceState('speaking');
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ru-RU';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    
    utterance.onend = () => {
      setVoiceState('idle');
    };
    
    utterance.onerror = () => {
      setVoiceState('error');
      setError('Ошибка синтеза речи');
    };
    
    synthRef.current.speak(utterance);
  };

  // Начать слушать
  const startListening = () => {
    if (!recognitionRef.current) return;
    
    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error('Ошибка запуска распознавания:', error);
      setError('Не удалось запустить распознавание речи');
      setVoiceState('error');
    }
  };

  // Остановить слушание
  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (synthRef.current) {
      synthRef.current.cancel();
    }
    setVoiceState('idle');
  };

  // Примеры команд
  const exampleCommands = [
    'Покормил стеллаж 1, полтора килограмма отрубей',
    'Температура 22 градуса, влажность 75 процентов',
    'Создать заказ для магазина Рыболов',
    'Показать статистику за месяц',
    'Сколько червей на втором стеллаже?',
  ];

  if (!isSupported) {
    return (
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <div className="fixed inset-0 bg-black bg-opacity-25" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg p-6">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 mr-2" />
                  <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Голосовые команды недоступны
                  </Dialog.Title>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Ваш браузер не поддерживает Web Speech API. Попробуйте Chrome или Safari.
                </p>
                <button
                  onClick={onClose}
                  className="mt-4 btn-primary"
                >
                  Понятно
                </button>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                {/* Заголовок */}
                <div className="flex items-center justify-between">
                  <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                    Голосовые команды
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded-md p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Основная область */}
                <div className="mt-4">
                  {/* Визуализация состояния */}
                  <div className="flex flex-col items-center mb-6">
                    <div
                      className={clsx(
                        'w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300',
                        {
                          'bg-gray-100 dark:bg-gray-700': voiceState === 'idle',
                          'bg-red-100 dark:bg-red-900/20 animate-pulse': voiceState === 'listening',
                          'bg-blue-100 dark:bg-blue-900/20 animate-spin': voiceState === 'processing',
                          'bg-green-100 dark:bg-green-900/20 animate-pulse': voiceState === 'speaking',
                          'bg-yellow-100 dark:bg-yellow-900/20': voiceState === 'error',
                        }
                      )}
                    >
                      {voiceState === 'speaking' ? (
                        <SpeakerWaveIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                      ) : voiceState === 'error' ? (
                        <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                      ) : (
                        <MicrophoneIcon
                          className={clsx(
                            'h-8 w-8',
                            voiceState === 'listening' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
                          )}
                        />
                      )}
                    </div>
                    
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {voiceState === 'idle' && 'Нажмите кнопку для начала'}
                      {voiceState === 'listening' && 'Слушаю...'}
                      {voiceState === 'processing' && 'Обрабатываю команду...'}
                      {voiceState === 'speaking' && 'Говорю ответ...'}
                      {voiceState === 'error' && 'Произошла ошибка'}
                    </p>
                  </div>

                  {/* Транскрипт */}
                  {transcript && (
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <span className="font-medium">Распознано:</span> {transcript}
                      </p>
                    </div>
                  )}

                  {/* Ответ */}
                  {response && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-300">
                        <span className="font-medium">Ответ:</span> {response}
                      </p>
                    </div>
                  )}

                  {/* Ошибка */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <p className="text-sm text-red-800 dark:text-red-300">
                        <span className="font-medium">Ошибка:</span> {error}
                      </p>
                    </div>
                  )}

                  {/* Кнопки управления */}
                  <div className="flex justify-center space-x-3 mb-6">
                    {voiceState === 'idle' || voiceState === 'error' ? (
                      <button
                        onClick={startListening}
                        className="btn-primary"
                      >
                        <MicrophoneIcon className="h-4 w-4 mr-2" />
                        Начать слушать
                      </button>
                    ) : (
                      <button
                        onClick={stopListening}
                        className="btn-danger"
                      >
                        Остановить
                      </button>
                    )}
                  </div>

                  {/* Примеры команд */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Примеры команд:
                    </h4>
                    <ul className="space-y-1">
                      {exampleCommands.map((command, index) => (
                        <li
                          key={index}
                          className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                          onClick={() => setTranscript(command)}
                        >
                          • {command}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default VoiceInterface;
